import { Mic, Sparkles, Zap, Target, MessageSquareQuote } from "lucide-react";

const DEMO_VOICE = {
  sampleCount: 8,
  cadenceScore: 62,
  writingPersonality: ["Witty", "Warm", "Direct", "Storyteller"],
  voiceModes: [
    { label: "Thought Leader", dominantTone: "Bold & Declarative", description: "Sharp opinions, minimal hedging", exampleContext: "LinkedIn, newsletters" },
    { label: "Friendly Expert", dominantTone: "Warm & Accessible", description: "Teaching without lecturing", exampleContext: "Explainer posts, email" },
    { label: "Off-duty", dominantTone: "Dry & Playful", description: "The wit comes out to play", exampleContext: "iMessage, Twitter/X" },
  ],
  tone: "Warm-Conversational",
  vocabularyComplexity: "Conversational-elevated",
  emojiUsage: "Sparingly — 1–2 per post max",
  humorLevel: "Playful, self-aware",
  storyPattern: "Opens direct → pivot → punchline",
  sentenceRhythm: "Short bursts with longer flows",
  formattingPersonality: "Occasional line breaks, no bullet walls",
  typingFeel: "deliberate",
  hookTaxonomy: "Hot take / bold claim",
  hookExamples: [
    "Hot take: the best career move I made wasn't a promotion.",
    "Nobody talks about this, but confidence is half editing.",
    "Genuine question: why do we still write emails like it's 2005?",
  ],
  ctaPattern: "Ends with a question to invite discussion",
  signaturePhrases: ["hot take:", "here's the thing —", "in a real sense", "not gonna lie"],
  powerWords: ["unmistakably", "spectacularly", "irreversibly", "quietly"],
  punctuationFingerprint: "Em-dash heavy, rare commas, no Oxford comma. Ellipsis for dramatic pause.",
  fillerWordCadence: "Heavy use of 'here's the thing', occasional self-referential humor",
  capitalizationQuirks: "Lowercase for ironic understatement ('…yeah')",
  sentenceFragments: [
    "Not ideal. But honest.",
    "Which, honestly, fair.",
    "Wild, right?",
  ],
  selfInterruptions: [
    "I know, I know —",
    "okay but hear me out —",
  ],
  characteristicOpeners: [
    "Hot take:",
    "Genuine question:",
    "Nobody asks about…",
    "Real talk —",
  ],
  openingPattern: "Opens with a provocative claim or vulnerable admission. Rarely sets context first.",
};

export default function VoiceProfileSnapshot() {
  return (
    <section className="py-20 px-4 sm:px-6 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 40%, hsl(188 90% 42% / 0.06) 0%, transparent 65%)",
        }}
      />
      <div className="max-w-5xl mx-auto">

        {/* Section headline */}
        <div className="text-center mb-10">
          <p className="text-xs text-cyan-400 font-semibold uppercase tracking-widest mb-3" style={{ filter: "drop-shadow(0 0 6px hsl(188 90% 42% / 0.55))" }}>
            Your Voice DNA
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Your voice,{" "}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              mapped and mastered
            </span>
          </h2>
          <p className="text-slate-400 text-base max-w-lg mx-auto">
            ScribPost builds a deep fingerprint of how you write — and uses it on every single post it generates for you.
          </p>
        </div>

        {/* Voice Profile Card — full-width, scrollable */}
        <div className="max-w-2xl mx-auto">
          <div
            className="rounded-3xl border border-cyan-500/20 glass-panel p-6 relative overflow-hidden"
            style={{
              background: "hsl(222 44% 5% / 0.97)",
              boxShadow: "0 0 80px hsl(188 90% 42% / 0.09), inset 0 1px 0 hsl(188 90% 80% / 0.05)",
            }}
          >
            {/* Card header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))", boxShadow: "0 0 16px hsl(188 90% 42% / 0.4)" }}
                >
                  <Mic className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white leading-tight">Your Voice Profile</p>
                  <p className="text-[11px] text-slate-400">Active · Fully trained</p>
                </div>
              </div>
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{
                  background: "hsl(142 70% 45% / 0.15)",
                  border: "1px solid hsl(142 70% 45% / 0.3)",
                  color: "hsl(142 70% 65%)",
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {DEMO_VOICE.sampleCount} samples trained
              </div>
            </div>

            {/* Voice Modes */}
            <div className="space-y-2 mb-5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <Zap className="w-3 h-3" /> Voice Modes
              </div>
              <p className="text-[10px] text-slate-600 leading-relaxed">
                Your writing clusters into distinct registers. Select one in the post generator to tune output to that mode.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {DEMO_VOICE.voiceModes.map((vm) => (
                  <div key={vm.label} className="group relative">
                    <span className="px-3 py-1.5 rounded-full text-xs font-medium border bg-cyan-500/8 border-cyan-500/25 text-cyan-400 cursor-default select-none block">
                      {vm.label}
                    </span>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-44 p-2.5 rounded-xl bg-popover border border-white/10 shadow-lg text-[10px] text-slate-400 leading-snug hidden group-hover:block z-10 pointer-events-none"
                      style={{ background: "hsl(222 44% 8%)" }}>
                      <p className="font-semibold text-white mb-1">{vm.dominantTone}</p>
                      <p className="mb-1">{vm.description}</p>
                      <p className="text-slate-500">Best for: {vm.exampleContext}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Writing Personality */}
            <div className="space-y-2 mb-5">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Personality</span>
              <div className="flex flex-wrap gap-1.5">
                {DEMO_VOICE.writingPersonality.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-cyan-500/10 text-cyan-400 text-xs font-medium rounded-full capitalize">{tag}</span>
                ))}
              </div>
            </div>

            {/* Cadence */}
            <div className="space-y-2 mb-5">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Cadence</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Punchy</span>
                <div className="flex-1 h-2 bg-white/8 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${DEMO_VOICE.cadenceScore}%`,
                      background: "linear-gradient(90deg, hsl(188 90% 42%), hsl(265 75% 62%))",
                    }}
                  />
                </div>
                <span className="text-xs text-slate-500">Flowing</span>
              </div>
            </div>

            {/* Trait rows */}
            <div className="space-y-3 pt-4 border-t border-white/8 mb-5">
              {[
                { label: "Primary Tone", value: DEMO_VOICE.tone },
                { label: "Vocabulary", value: DEMO_VOICE.vocabularyComplexity },
                { label: "Emoji Usage", value: DEMO_VOICE.emojiUsage },
                { label: "Humor", value: DEMO_VOICE.humorLevel },
                { label: "Story Pattern", value: DEMO_VOICE.storyPattern },
                { label: "Sentence Rhythm", value: DEMO_VOICE.sentenceRhythm },
                { label: "Formatting", value: DEMO_VOICE.formattingPersonality },
                { label: "Typing Feel", value: DEMO_VOICE.typingFeel },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-start gap-2 text-sm">
                  <span className="text-slate-500 text-xs">{label}</span>
                  <span className="font-medium capitalize text-right max-w-[55%] text-xs leading-snug text-slate-200 shrink-0">{value}</span>
                </div>
              ))}
            </div>

            {/* Hook Style */}
            <div className="space-y-2 pt-4 border-t border-white/8 mb-5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <Zap className="w-3 h-3" /> Hook Style
              </div>
              <p className="text-sm text-white">{DEMO_VOICE.hookTaxonomy}</p>
              <div className="space-y-1 mt-2">
                <span className="text-[10px] font-medium text-slate-600 uppercase tracking-wider">Example Openers</span>
                {DEMO_VOICE.hookExamples.map((ex, i) => (
                  <p key={i} className="text-xs text-slate-300 pl-3 border-l-2 border-cyan-500/30 italic">{ex}</p>
                ))}
              </div>
            </div>

            {/* CTA Pattern */}
            <div className="space-y-1.5 mb-5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <Target className="w-3 h-3" /> CTA Pattern
              </div>
              <p className="text-sm text-slate-200">{DEMO_VOICE.ctaPattern}</p>
            </div>

            {/* Signature Phrases */}
            <div className="space-y-2 pt-4 border-t border-white/8 mb-5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <MessageSquareQuote className="w-3 h-3" /> Signature Phrases
              </div>
              <div className="flex flex-wrap gap-1.5">
                {DEMO_VOICE.signaturePhrases.map((phrase, i) => (
                  <span key={i} className="px-2 py-1 rounded-md text-xs"
                    style={{ background: "hsl(188 90% 42% / 0.1)", border: "1px solid hsl(188 90% 42% / 0.2)", color: "hsl(188 90% 72%)" }}>
                    "{phrase}"
                  </span>
                ))}
              </div>
            </div>

            {/* Power Words */}
            <div className="space-y-2 mb-5">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Power Words</span>
              <div className="flex flex-wrap gap-1.5">
                {DEMO_VOICE.powerWords.map((word, i) => (
                  <span key={i} className="px-2 py-1 bg-amber-500/10 text-amber-400 text-xs font-medium rounded-md border border-amber-500/20">{word}</span>
                ))}
              </div>
            </div>

            {/* Punctuation Fingerprint */}
            <div className="space-y-1.5 pt-4 border-t border-white/8 mb-5">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Punctuation Fingerprint</span>
              <p className="text-xs text-slate-300 leading-relaxed">{DEMO_VOICE.punctuationFingerprint}</p>
            </div>

            {/* Verbal Tics */}
            <div className="space-y-1.5 mb-5">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Verbal Tics</span>
              <p className="text-xs text-slate-300">{DEMO_VOICE.fillerWordCadence}</p>
            </div>

            {/* Capitalization */}
            <div className="flex justify-between items-start text-sm mb-5">
              <span className="text-slate-500 text-xs">Capitalization</span>
              <span className="font-medium text-right max-w-[60%] text-xs leading-snug text-slate-200">{DEMO_VOICE.capitalizationQuirks}</span>
            </div>

            {/* Quirky Fragments */}
            <div className="space-y-2 mb-5">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Quirky Fragments</span>
              <div className="space-y-1">
                {DEMO_VOICE.sentenceFragments.map((frag, i) => (
                  <p key={i} className="text-xs text-slate-300 pl-3 border-l-2 border-cyan-500/30 italic">"{frag}"</p>
                ))}
              </div>
            </div>

            {/* Self-Interruptions */}
            <div className="space-y-2 mb-5">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Self-Interruptions</span>
              <div className="space-y-1">
                {DEMO_VOICE.selfInterruptions.map((si, i) => (
                  <p key={i} className="text-xs text-slate-300 pl-3 border-l-2 border-amber-400/35 italic">"{si}"</p>
                ))}
              </div>
            </div>

            {/* Characteristic Openers */}
            <div className="space-y-2 pt-4 border-t border-white/8 mb-5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <span>Characteristic Openers</span>
                <span className="normal-case font-normal text-slate-600">— verbatim from your samples</span>
              </div>
              <div className="space-y-1">
                {DEMO_VOICE.characteristicOpeners.map((opener, i) => (
                  <p key={i} className="text-xs text-slate-300 pl-3 border-l-2 border-emerald-400/35 italic">"{opener}"</p>
                ))}
              </div>
            </div>

            {/* Opening Pattern */}
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Opening Pattern</span>
              <p className="text-xs text-slate-300 leading-relaxed">{DEMO_VOICE.openingPattern}</p>
            </div>

            {/* Subtle footer tag */}
            <div className="mt-6 pt-4 border-t border-white/8 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-cyan-400/70" />
              <span className="text-[11px] text-slate-600">Built from real writing samples — not a quiz</span>
            </div>

            {/* Glow overlay */}
            <div
              className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none rounded-b-3xl"
              style={{ background: "linear-gradient(to top, hsl(188 90% 42% / 0.03), transparent)" }}
            />
          </div>
        </div>

      </div>
    </section>
  );
}
